import { DiagonalDirection, FrenzyLine } from "../Model";
import { minMax } from "../Utils";

export type HitBox = {
  boxSize: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number
};
export function hitBox(x: number, y: number, boxSize: number): HitBox {
  return {
    boxSize,
    minX: x - boxSize,
    minY: y - boxSize,
    maxX: x + boxSize,
    maxY: y + boxSize,
  };
}

export function describeHitBox(hb: HitBox): string {
  return `${hb.minX},${hb.minY}<->${hb.maxX},${hb.maxY}`;
}

/** 
 * Creates a hitbox where the x,y is the "leading corner" according to the direction being travelled,
 * and the rest of the box comes off the trailing edges of that.
 * 
 * e.g. if travelling South-west, with x,y of (100,200) and boxSize 3, the hitbox would be:
 * minX: x,
 * minY: y - 3,
 * maxX: x + 3,
 * maxY: y 
 */ 
export function trailingHitBox(x: number, y: number, direction: DiagonalDirection, boxSize: number): HitBox {
  let minX = x;
  let minY = y;
  let maxX = x;
  let maxY = y;
  if (direction.label === "NW" || direction.label === "SW") {
    maxX = x + boxSize;
  } else {
    minX = x - boxSize;
  }
  if (direction.label === "NW" || direction.label === "NE") {
    maxY = y + boxSize;
  } else {
    minY = y - boxSize;
  }
  return {
    boxSize,
    minX,
    minY,
    maxX,
    maxY,
  };
}

export function lineToHitBox(line: FrenzyLine): HitBox {
  const [minX, maxX] = minMax(line.start[0], line.end[0]);
  const [minY, maxY] = minMax(line.start[1], line.end[1]);
  const boxSize = maxX - minX
  return {
    boxSize,
    minX,
    minY,
    maxX,
    maxY,
  };
}

export function areHitBoxesColliding(hbA: HitBox, hbB: HitBox): boolean {
  // Look for immediately-disjoint boxes:
  if ((hbA.maxX < hbB.minX) || (hbA.minX > hbB.maxX) ||
       (hbA.maxY < hbB.minY) || (hbA.minY > hbB.maxY)) {
    return false;
  }

  return true;
}

// These two functions envy each other but the boolean-returning one is fast, so ... ?

export function areLinesIntersectingHitBox(lines: Array<FrenzyLine>, hitBox: HitBox):boolean {
  // First, make a hitbox out of each line. If every single one fails the colliding test,
  // we're not hitting

  const lineHitBoxes = lines.map(lineToHitBox);

  for (const lineHitBox of lineHitBoxes) {
    if (areHitBoxesColliding(lineHitBox, hitBox)) {
      return true;
    }
  }
  return false;
}

export function findLinesIntersectingHitBox(lines: Array<FrenzyLine>, hitBox: HitBox):Array<FrenzyLine> {
  // First, make a hitbox out of each line. If every single one fails the colliding test,
  // we're not hitting

  const linesWithTheirHitBoxes = lines.map((line) => {
    return {
      line,
      hb: lineToHitBox(line)
    };
  });


  return linesWithTheirHitBoxes.filter(({ line, hb}) => {
    if (areHitBoxesColliding(hb, hitBox)) {
      return true;
    }
  }).map(({line, hb}) => line);
}